import type {
  WorkplaceRequestReservationFieldConfig,
  WorkplaceRequestTypeFieldType
} from "@gettactic/api";
import { format } from "date-fns";
import { isValidTime } from "../date-time/isValidTime";
import { time12To24 } from "../date-time/timeStringConverters";
import { EVERY_RESERVATION_PART } from "./reservationField";
import {
  isReservationFieldFormValue,
  toReservationFieldValue
} from "./reservationFieldValue";

/**
 * The value the create/update payload expects for one answer, from what the
 * form holds. The write counterpart of {@link toWorkplaceRequestFormValue}.
 *
 * Three field types are held in a shape the API won't take:
 *
 * - A `date` question answers a calendar day: core stores and returns a bare
 *   `YYYY-MM-DD` and rejects the whole request with a 400 when it is handed an
 *   instant instead. The calendar input works in `Date`s, and serializing one
 *   straight into JSON yields a UTC timestamp — which both failed the request
 *   and could name the wrong day. The day is therefore read off the `Date` in
 *   the browser's own zone: it was built as local midnight of the day that was
 *   clicked, so that is the day the user meant.
 * - A `time` question answers a time of day, which core stores as 24-hour
 *   `HH:MM` while the shared time picker works in 12-hour strings.
 * - A `reservation` question answers a whole booking, and needs `config` to
 *   know how much of it may be sent — see {@link toReservationFieldValue}.
 *
 * Every other field type is already held in the shape the API wants.
 */
export const toWorkplaceRequestFieldValue = (
  fieldType: WorkplaceRequestTypeFieldType,
  value: unknown,
  /** The field's own switches. Only a `reservation` field reads it. */
  config?: WorkplaceRequestReservationFieldConfig
): unknown => {
  if (fieldType === "reservation") {
    // An unanswered question arrives as undefined, or as whatever the form was
    // seeded with; either way there is nothing to send.
    if (!isReservationFieldFormValue(value)) return null;
    return toReservationFieldValue(value, config ?? EVERY_RESERVATION_PART);
  }

  if (fieldType === "time") {
    if (typeof value !== "string" || !value) return value;
    // Already 24-hour: read back from a request and never touched.
    if (isValidTime(value)) return value;
    // Not something core will accept. `time12To24` answers "00:00" to anything
    // it can't match, so an unparseable answer has to be cleared here or it is
    // silently filed as midnight. Null routes through the caller's empty check,
    // mirroring how an invalid `Date` is handled for `date`.
    return isValidTime(value, true) ? time12To24(value) : null;
  }

  if (fieldType !== "date") return value;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : format(value, "yyyy-MM-dd");
  }
  // Anything else is left alone: a `YYYY-MM-DD` read back from a request and
  // never touched is already what the API wants, and an empty answer is dropped
  // by the caller before it is sent.
  return value;
};
